import {useCallback, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useMe} from '../../../common/hooks/useMe'
import {executeRecaptcha} from '../../utils/recaptcha'
import {startVideoToGifJob, pollConverterJob, verifyConverterCaptcha} from '../../../common/api/converterVideo'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {reportError} from '../../../common/errors/reportError'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {downloadFile} from '../../../common/utils/downloadFile'
import {JOB_POLL_INTERVAL_MS} from '../../../common/constants/videoToGifLimits'
import type {LoadedVideo} from './loadConverterVideoFile'
import ConverterExportDialog from '../converterShared/ConverterExportDialog'
import {CONVERTER_AD_FEATS} from '../converterShared/adFeatIcons'

interface VideoDownloadPanelProps {
  video: LoadedVideo | null
  trimStart: number
  trimEnd: number
  longEdgePx: number
  fps: number
  colors: number
}

type Phase = 'idle' | 'verifying' | 'busy'

const fetchAsBlob = async (url: string): Promise<Blob> => {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Download fetch failed: ${res.status}`)
  return res.blob()
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/** Percent of the overall progress bar spent on the upload phase — without this
 * the bar sat at 0% for however long the (potentially large) video took to
 * upload, then jumped straight to real ffmpeg progress once the job started,
 * looking uneven/stuck. Upload and conversion are weighted evenly since neither
 * reliably dominates the other across the size/settings range this tool allows. */
const UPLOAD_PROGRESS_WEIGHT_PERCENT = 30

const VideoDownloadPanel = ({video, trimStart, trimEnd, longEdgePx, fps, colors}: VideoDownloadPanelProps) => {
  const {t} = useTranslation()
  const {me} = useMe()
  const [phase, setPhase] = useState<Phase>('idle')
  const [creditedProgress, setCreditedProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const ticketRef = useRef<string | null>(null)

  const hasCredits = me !== null && me.credits > 0
  const trimmedDurationSec = trimEnd - trimStart

  // Starts the async BE job, then polls GET /converter/jobs/:id for REAL progress
  // (parsed server-side from ffmpeg's own -progress output, not a heuristic) until
  // the job settles. reportProgress is optional so this doubles as the credited
  // fast-path's generator (button label) and the gated dialog's onGenerate.
  const generate = useCallback(
    async (reportProgress?: (percent: number) => void): Promise<Blob> => {
      const ticket = ticketRef.current
      if (!ticket || !video) throw new Error('Missing verification ticket or source video')
      const {jobId} = await startVideoToGifJob(
        {
          videoFile: video.file,
          startSec: trimStart,
          durationSec: trimmedDurationSec,
          longEdgePx,
          fps,
          colors,
          ticket,
        },
        (uploadFraction) => reportProgress?.(Math.round(uploadFraction * UPLOAD_PROGRESS_WEIGHT_PERCENT)),
      )

      for (;;) {
        const job = await pollConverterJob(jobId)
        if (job.status === 'processing') {
          const overall = UPLOAD_PROGRESS_WEIGHT_PERCENT + job.progress * (100 - UPLOAD_PROGRESS_WEIGHT_PERCENT)
          reportProgress?.(Math.round(overall))
          await wait(JOB_POLL_INTERVAL_MS)
          continue
        }
        if (job.status === 'error') throw new Error(job.error ?? 'Conversion failed')
        reportProgress?.(100)
        if (!job.downloadUrl) throw new Error('Missing download URL')
        return fetchAsBlob(job.downloadUrl)
      }
    },
    [video, trimStart, trimmedDurationSec, longEdgePx, fps, colors],
  )

  const startConversion = useCallback(async () => {
    if (hasCredits) {
      setPhase('busy')
      setCreditedProgress(0)
      trackEvent('video_to_gif_export_started', {gated: false})
      try {
        const blob = await generate(setCreditedProgress)
        await downloadFile(blob, 'converted.gif')
        trackEvent('video_to_gif_export_completed', {size_bytes: blob.size, gated: false})
      } catch (err) {
        reportError(err, 'video-to-gif-convert', {gated: false})
        setError(t('videoToGif.download.failed'))
        trackEvent('video_to_gif_export_failed', {gated: false})
      } finally {
        setPhase('idle')
      }
      return
    }
    setPhase('idle')
    setDialogOpen(true)
  }, [hasCredits, generate, t])

  const handleDownloadClick = useCallback(async () => {
    if (!video) return
    trackEvent('video_to_gif_download_clicked', {has_credits: hasCredits})
    setPhase('verifying')
    setError(null)
    try {
      const tokenV3 = await executeRecaptcha('video_to_gif')
      const result = await verifyConverterCaptcha(tokenV3)
      if (result.ok && result.ticket) {
        ticketRef.current = result.ticket
        addBreadcrumb('video-to-gif', 'captcha-verified', 'info', {})
        await startConversion()
        return
      }
      setPhase('idle')
      setError(t('videoToGif.download.recaptchaFailed'))
    } catch (err) {
      reportError(err, 'video-to-gif-verify')
      setPhase('idle')
      setError(t('videoToGif.download.recaptchaFailed'))
    }
  }, [video, hasCredits, startConversion, t])

  const handleDialogClose = useCallback(() => setDialogOpen(false), [])

  const disabled = !video || phase === 'verifying' || phase === 'busy'

  return (
    <div className="vtg-download">
      <button type="button" className="cvt-btn cvt-btn-primary" onClick={handleDownloadClick} disabled={disabled}>
        {phase === 'verifying' && t('videoToGif.download.verifying')}
        {phase === 'busy' && t('videoToGif.download.busyProgress', {percent: creditedProgress})}
        {phase !== 'verifying' && phase !== 'busy' && t('videoToGif.download.cta')}
      </button>

      {error && <p className="cvt-error">{error}</p>}
      {!video && <p className="cvt-hint">{t('videoToGif.download.needVideo')}</p>}

      {dialogOpen && (
        <ConverterExportDialog
          namespace="videoToGif"
          breadcrumbCategory="video-to-gif"
          analyticsSurface="video_to_gif"
          startedEvent="video_to_gif_export_started"
          completedEvent="video_to_gif_export_completed"
          progressMode="real"
          filename="converted.gif"
          showAd
          adFeats={CONVERTER_AD_FEATS}
          adCtaTo="/free"
          onGenerate={generate}
          onClose={handleDialogClose}
        />
      )}
    </div>
  )
}

export default VideoDownloadPanel
